import { IBusinessEntity } from '@commenda/types';
import {
  JurisdictionInterface,
  SupportedCorporationInfo,
  SupportedJurisdictionsInfo,
} from '@commenda/types/src/corporation';
import { CorporationType, Country } from '@prisma/client';
import { getPrintableCorporationType } from '../corporation-utils';

export class CorporationModel {
  static getJurisdictionList(
    country: Country,
    supportedCorporations: SupportedCorporationInfo
  ): { id: string; displayName: string }[] {
    if (!country || !supportedCorporations) {
      return [];
    }

    return Object.values(supportedCorporations?.jurisdictions[country] || {})
      .filter((j): j is JurisdictionInterface => Boolean(j?.id))
      .map((j) => ({ id: j.id, displayName: j.displayName }));
  }

  static getEntityTypes(
    country: Country,
    jurisdiction: string,
    supportedCorporations: SupportedCorporationInfo
  ): { code: CorporationType; displayName: string }[] {
    if (!country || !jurisdiction) return [];

    return Object.keys(
      supportedCorporations?.corporations[country]?.[jurisdiction] || {}
    ).map((corporationType) => {
      return {
        code: corporationType as CorporationType,
        displayName: getPrintableCorporationType(
          corporationType as CorporationType,
          country
        ),
      };
    });
  }

  /**
   * The legal forms a jurisdiction offers, named as the content names them.
   *
   * Keyed by legal form id rather than corporation type. `corporations` can only hold a form the
   * legacy enum has a name for and silently drops the rest, which leaves 40 of the 86 countries
   * the content offers with nothing to pick at all.
   */
  static getEntityTypeOptions(
    country: Country,
    jurisdiction: string,
    supportedCorporations: SupportedCorporationInfo
  ): {
    entityLegalFormId: string;
    displayName: string;
    corporationType: CorporationType | null;
  }[] {
    if (!country || !jurisdiction) return [];

    return Object.values(
      supportedCorporations?.entityTypes?.[country]?.[jurisdiction] || {}
    )
      .map((form) => ({
        entityLegalFormId: form.entityLegalFormId,
        // The id is a poor label, but it is specific and stable — better than an empty row.
        displayName:
          form.appCopy?.corporationTypeFullName || form.entityLegalFormId,
        corporationType: form.corporationType ?? null,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  static isRegisteredAgentSupported(businessEntity: IBusinessEntity) {
    return (
      businessEntity.generalCorporationInfo.incorporationCountry === Country.US
    );
  }

  static isSupportedEntityManagement(
    country: Country,
    supportedJurisdictions:
      | SupportedJurisdictionsInfo['jurisdictions']
      | undefined
  ): boolean {
    return Boolean(supportedJurisdictions?.[country]);
  }

  static isMailRoomSupported(businessEntity: IBusinessEntity) {
    // This is just a demo code and we will remove it later. Do not use in production.
    const isDemo =
      process.env.NEXT_PUBLIC_CLIENT_ENV === 'demo' ||
      process.env.NEXT_PUBLIC_CLIENT_ENV === 'development';
    if (isDemo) return true;
    return (
      businessEntity.generalCorporationInfo.incorporationCountry === Country.US
    );
  }
}
